import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Typography, Alert, CircularProgress, Divider, Box, IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { businessAPI } from '../api';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import SchedulePicker from './SchedulePicker';
import { formatSchedule } from '../utils/schedule';
import ScheduleChangeConfirm, { daysChanged } from './ScheduleChangeConfirm';

/**
 * Create or rename a delivery plan for a product.
 *
 * A plan is the same product delivered on set days — "Sunday Curd" sells Curd, on Sundays. It
 * has no price of its own: price, day prices and discounts all come from the product, so the
 * vendor only ever maintains one price per product.
 *
 * props:
 *   businessId
 *   product   the parent product a new plan sells (create mode)
 *   plan      an existing plan to edit (edit mode; product is then its parent)
 *   onClose(saved)  saved=true when something changed and the caller should reload
 */
const PlanFormDialog = ({ businessId, product, plan, open, onClose }) => {
    const isEdit = Boolean(plan);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [confirmDays, setConfirmDays] = useState(false);

    useEffect(() => {
        if (!open) return;
        setError('');
        setConfirmDays(false);
        if (isEdit) {
            setForm({
                f_name: plan.f_name,
                f_schedule_type: plan.f_schedule_type || 'daily',
                f_schedule_value: plan.f_schedule_value ?? 1,
                f_anchor_date: plan.f_anchor_date || null,
            });
        } else {
            const initial = { f_schedule_type: 'weekly', f_schedule_value: 0, f_anchor_date: null };
            setForm({ ...initial, f_name: suggestName(product, initial) });
        }
    }, [open, isEdit, plan, product]);

    // "Sunday Curd" from the product and the days — editable, so a vendor can name it their way.
    function suggestName(prod, schedule) {
        if (!prod || (schedule.f_schedule_type === 'weekly' && !schedule.f_schedule_value)) return '';
        const label = formatSchedule(schedule);
        if (label === 'Every day') return `Daily ${prod.f_name}`;
        if (label.startsWith('Every ')) return `${label.replace('Every ', '')} ${prod.f_name}`;
        return `${prod.f_name} — ${label}`;
    }

    const onScheduleChange = (schedule) => {
        setForm((f) => {
            const next = { ...f, ...schedule };
            // Only rewrite a name the vendor hasn't typed over themselves.
            if (!isEdit && (!f.f_name || f.f_name === suggestName(product, f))) {
                next.f_name = suggestName(product, schedule);
            }
            return next;
        });
    };

    const customerCount = plan?.active_customer_count || 0;
    const handleSaveClick = () => {
        if (isEdit && customerCount > 0 && daysChanged(plan, form)) setConfirmDays(true);
        else handleSave();
    };

    const handleSave = async () => {
        setConfirmDays(false);
        setSaving(true);
        setError('');
        try {
            const payload = {
                f_name: form.f_name.trim(),
                f_schedule_type: form.f_schedule_type,
                f_schedule_value: form.f_schedule_value,
                f_anchor_date: form.f_schedule_type === 'interval' ? form.f_anchor_date : null,
            };
            if (isEdit) {
                await businessAPI.updatePlan(businessId, plan.f_id, payload);
            } else {
                await businessAPI.createPlan(businessId, {
                    ...payload, f_business_product_id: product.f_id,
                });
            }
            onClose(true);
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not save this plan. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (!form) return null;

    const noDaysPicked = form.f_schedule_type === 'weekly' && !form.f_schedule_value;
    const canSave = form.f_name.trim() && !noDaysPicked;
    const parentName = isEdit ? (plan.parent_product_name || '') : (product?.f_name || '');

    return (
        <Dialog open={open} onClose={() => onClose(false)} {...APP_DIALOG}>
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {isEdit ? 'Edit Plan' : 'New Plan'}
                <IconButton size="small" onClick={() => onClose(false)} sx={{ color: 'white' }}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Deliver <strong>{parentName}</strong> only on certain days.
                </Typography>

                <TextField
                    fullWidth size="small" label="Name this plan"
                    value={form.f_name}
                    onChange={(e) => setForm({ ...form, f_name: e.target.value })}
                />

                <Box sx={{ mt: 2 }}>
                    <SchedulePicker value={form} onChange={onScheduleChange} />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                    The price comes from {parentName}. Change the price on the product.
                </Typography>

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={() => onClose(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveClick} disabled={saving || !canSave}>
                    {saving ? <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" /> : null}
                    {isEdit ? 'Save' : 'Create Plan'}
                </Button>
            </DialogActions>
            <ScheduleChangeConfirm
                open={confirmDays}
                schedule={form}
                customerCount={customerCount}
                kind="plan"
                onCancel={() => setConfirmDays(false)}
                onConfirm={handleSave}
            />
        </Dialog>
    );
};

export default PlanFormDialog;
